import { getActiveLanguageCode } from 'services/i18nService';
import { CAMS, COUPLE } from 'enum/helpers';
import { getTranslatedGender } from 'utils/gender';


export default class MetaService {
  /** Retrieves the translation keys for the current url
   * @param  {} field String 'title' or 'description'
   */
  getMetaTranslation = (field, match) => {
    const key = this.#computeKey(match);
    if (!key) {
      return '';
    }

    const translationKey = `metatags.${key}.${field}`;

    return window.i18nDictionary[translationKey] || '';
  };

  #computeKey = (match) => {
    const { filter, genderPreference, tag } = match.params;

    const pathFilters = filter || tag || '';

    const gender = this.#computeGenderKey(genderPreference, pathFilters);
    const rootKey = gender ? `metatags.${gender}` : 'metatags';

    if (!pathFilters) {
      return rootKey;
    }

    const translationKey = pathFilters.replace(`${COUPLE}/`, '');

    return `${rootKey}.${translationKey}`;
  };

  #computeGenderKey = (genderPreference, pathFilters) => {
    const languageCode = getActiveLanguageCode();

    if (languageCode === 'en') {
      const hasCoupleFilter = pathFilters.startsWith(COUPLE);
      if (genderPreference === CAMS && hasCoupleFilter) {
        return COUPLE;
      }

      return genderPreference;
    }

    const genders = getTranslatedGender(languageCode) || {};

    for (const [genderKey, genderValue] of Object.entries(genders)) {
      if ([genderKey, genderValue].includes(genderPreference)) {
        return genderKey;
      }
    }

    return '';
  };

  /**
   *  Sets the value of the description and title metatags for directory
   * @param {String} descriptionValue - Meta description value
   * @param {String }titleValue - Meta title value
   */
  setMetaContent = (descriptionValue, titleValue) => {
    const description = document.querySelector('meta[name="description"]');
    const title = document.querySelector('title');
    const metaTitle = document.querySelector('meta[name="title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');

    let twitterTextTitle = document.querySelector('meta[name="twitter:text:title"]');
    if (!twitterTextTitle) {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:text:title');
      document.head.appendChild(meta);

      twitterTextTitle = document.querySelector('meta[name="twitter:text:title"]');
    }

    if (description && ogDescription && twitterDescription) {
      description.setAttribute('content', descriptionValue);
      ogDescription.setAttribute('content', descriptionValue);
      twitterDescription.setAttribute('content', descriptionValue);
    }
    if (title && ogTitle && metaTitle && twitterTitle) {
      document.title = titleValue;
      ogTitle.setAttribute('content', titleValue);
      metaTitle.setAttribute('content', titleValue);
      twitterTitle.setAttribute('content', titleValue);
      twitterTextTitle.setAttribute('content', titleValue);
    }
  };

  setMetaDescription = (descriptionValue) => {
    const description = document.querySelector('meta[name="description"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (description && ogDescription) {
      description.setAttribute('content', descriptionValue);
      ogDescription.setAttribute('content', descriptionValue);
    }
    if (twitterDescription) {
      twitterDescription.setAttribute('content', descriptionValue);
    }
  };

  setMetaTitle = (titleValue) => {
    const title = document.querySelector('title');
    const metaTitle = document.querySelector('meta[name="title"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (title && ogTitle && metaTitle) {
      document.title = titleValue;
      ogTitle.setAttribute('content', titleValue);
      metaTitle.setAttribute('content', titleValue);
    }
    if (twitterTitle) {
      if (document.title !== titleValue) {
        document.title = titleValue;
      }
      twitterTitle.setAttribute('content', titleValue);
    }
  };

  setMetaUrl = (directoryList) => {
    const urlElement = document.querySelector('meta[property="og:url"]');
    const imageUrlElement = document.querySelector('meta[property="og:image"]');
    const twitterImageUrlElement = document.querySelector('meta[name="twitter:image"]');
    if (urlElement) {
      urlElement.setAttribute('content', window.location.href);
    }

    if (directoryList.users !== undefined && directoryList.users.length) {
      let imageUrl = undefined;
      const NUM_OF_USERS_TO_TRY = 5;
      // In case the first featured cam doesn't have a profile image...
      // ...attempt to take profile image from the second one, and so on
      for (
        let currentUserId = 0;
        currentUserId < NUM_OF_USERS_TO_TRY && currentUserId < directoryList.users.length;
        currentUserId += 1
      ) {
        const currentUser = directoryList.users[currentUserId];
        if (currentUser.profileImageLink) {
          imageUrl = currentUser.profileImageLink;
          break;
        } else if (currentUser.profileImageLinkSFW) {
          imageUrl = currentUser.profileImageLinkSFW;
          break;
        }
      }

      if (imageUrl) {
        if (imageUrlElement) {
          imageUrlElement.setAttribute('content', imageUrl);
        }
        if (twitterImageUrlElement) {
          twitterImageUrlElement.setAttribute('content', imageUrl);
        }
      }
      // TODO: if there is still no imageURL, increase the NUM_OF_USERS_TO_TRY, or we should have a default url fallback
    }
  };

  setMetaURL = () => {
    const url = document.querySelector('meta[property="og:url"]');
    if (url) {
      url.setAttribute('content', window.location.href);
    }
  };

  setMetaImage = (image) => {
    const imageUrl = document.querySelector('meta[property="og:image"]');
    const twitterImageUrl = document.querySelector('meta[name="twitter:image"]');
    imageUrl.setAttribute('content', image);
    twitterImageUrl.setAttribute('content', image);
  };

  /**
   *  Sets custom meta based on input
   * @param {Object} metaObject - Meta object -> { ogType: "video.other" }
   */
  setCustomMeta = (metaObject) => {
    Object.keys(metaObject).forEach((metaKey) => {
      // change ogType to => og:type
      const property = metaKey
        .split(/(?=[A-Z])/)
        .join(':')
        .toLocaleLowerCase();

      const type = property.startsWith('twitter:') ? 'name' : 'property';

      const metaItem = document.querySelector(`meta[${type}="${property}"]`);
      if (!metaItem) {
        this.createNewMeta(property, metaObject[metaKey]);
      } else {
        metaItem.setAttribute('content', metaObject[metaKey]);
      }
    });
  };

  createNewMeta = (name, content) => {
    const meta = document.createElement('meta');
    meta.name = name;
    meta.content = content;
    document.head.appendChild(meta);
  };

  setMeta = (name, content) => {
    const metaItem = document.getElementsByName(name)[0];
    if (metaItem) {
      if (metaItem.getAttribute('content') !== content) {
        metaItem.content = content;
      }
    } else {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.appendChild(meta);
    }
  };

  removeMetaUrl = () => {
    const url = document.querySelector('meta[property="og:url"]');
    const imageUrl = document.querySelector('meta[property="og:image"]');
    const twitterImageUrl = document.querySelector('meta[name="twitter:image"]');
    if (url) {
      url.setAttribute('content', '');
    }

    if (imageUrl) {
      imageUrl.setAttribute('content', '');
    }

    if (twitterImageUrl) {
      twitterImageUrl.setAttribute('content', '');
    }
  };
}
